import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import sr from '@utils/sr';
import { srConfig } from '@config';
import { usePrefersReducedMotion } from '@hooks';

const StyledWritingSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;

  h2 {
    font-size: clamp(24px, 5vw, var(--fz-heading));
  }

  .archive-link {
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    &:after {
      bottom: 0.1em;
    }
  }

  .posts-container {
    list-style: none;
    padding: 0;
    margin: 0;
    font-size: var(--fz-lg);
    width: 100%;
  }

  .post-inner {
    ${({ theme }) => theme.mixins.boxShadow};
    ${({ theme }) => theme.mixins.flexBetween};
    align-items: flex-start;
    position: relative;
    padding: 2rem 1.75rem;
    border-radius: var(--border-radius);
    background-color: var(--light-navy);
    transition: var(--transition);
    cursor: default;

    &:hover,
    &:focus {
      box-shadow: 0 20px 30px -15px var(--navy-shadow);
    }

    a {
      position: static;
      z-index: 1;

      &:before {
        content: '';
        display: block;
        position: absolute;
        z-index: 0;
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
      }
    }

    .post-header {
      ${({ theme }) => theme.mixins.flexBetween};
      align-items: flex-start;
      margin-bottom: 35px;
    }

    .post-title {
      margin: 0 0 10px;
      color: var(--lightest-slate);
      font-size: var(--fz-xxl);

      a {
        position: static;

        &:before {
          content: '';
          display: block;
          position: absolute;
          z-index: 0;
          width: 100%;
          height: 100%;
          top: 0;
          left: 0;
        }
      }
    }

    .post-description {
      color: var(--light-slate);
      font-size: 17px;

      a {
        ${({ theme }) => theme.mixins.inlineLink};
      }
    }

    .post-date {
      color: var(--light-slate);
      font-family: var(--font-mono);
      font-size: var(--fz-xxs);
      text-transform: uppercase;
    }

    ul.post-tech-list {
      display: flex;
      align-items: flex-end;
      flex-grow: 1;
      flex-wrap: wrap;
      padding: 0;
      margin: 20px 0 0 0;
      list-style: none;

      li {
        font-family: var(--font-mono);
        font-size: var(--fz-xxs);
        line-height: 1.75;

        &:not(:last-of-type) {
          margin-right: 15px;
        }
      }
    }
  }
`;

const Writing = () => {
  const revealTitle = useRef(null);
  const revealPosts = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealTitle.current, srConfig());
    revealPosts.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 100)));
  }, []);

  // Sample writing/articles data - you can replace this with actual content
  const posts = [
    {
      title: 'Building Accessible Web Applications with React',
      description:
        'A comprehensive guide to implementing WCAG 2.1 AA standards in modern React applications, covering semantic HTML, ARIA attributes, and testing strategies.',
      date: '2024',
      url: '#',
      tech: ['React', 'Accessibility', 'WCAG'],
    },
    {
      title: 'Firebase Security Rules: Best Practices',
      description:
        'Essential security patterns and rules for protecting your Firebase applications, including authentication flows and data validation.',
      date: '2024',
      url: '#',
      tech: ['Firebase', 'Security', 'JavaScript'],
    },
    {
      title: 'Modern CSS Layout Techniques',
      description:
        'Exploring CSS Grid, Flexbox, and modern layout patterns for creating responsive, maintainable web interfaces.',
      date: '2023',
      url: '#',
      tech: ['CSS', 'Web Design', 'Responsive'],
    },
  ];

  return (
    <StyledWritingSection id="writing">
      <h2 className="numbered-heading" ref={revealTitle}>
        Writing
      </h2>

      <ul className="posts-container">
        {posts &&
          posts.map(({ title, description, date, url, tech }, i) => (
            <li key={i} ref={el => (revealPosts.current[i] = el)}>
              <div className="post-inner">
                <header className="post-header">
                  <div className="post-top">
                    <div className="post-date">{date}</div>
                    <h3 className="post-title">
                      <a href={url}>{title}</a>
                    </h3>
                    <div className="post-description">{description}</div>
                  </div>
                </header>
                {tech && (
                  <ul className="post-tech-list">
                    {tech.map((tech, i) => (
                      <li key={i}>{tech}</li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
          ))}
      </ul>
    </StyledWritingSection>
  );
};

export default Writing;
